import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAraAuth } from '../contexts/AraAuthContext';
import { useAraActiveSession } from '../contexts/AraActiveSessionContext';
import { useAraTheme } from '../contexts/AraThemeContext';
import {
  saveAraEncrypted, getAllAraEncrypted, ARA_STORES,
  getAraAppState,
} from '../utils/ara-storage';
import {
  PaperPlaneTilt, ArrowLeft, BookmarkSimple, Sun, Moon,
  SignOut, NotePencil, Sparkle, Plus, ChatCircleDots, Gear, Heart,
} from 'phosphor-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const stripMarkdown = (t) => {
  if (!t) return '';
  return t
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/__(.+?)__/g, '$1')
    .replace(/(^|[\s(])\*([^*\n]+?)\*(?=[\s.,!?)]|$)/g, '$1$2')
    .replace(/(^|[\s(])_([^_\n]+?)_(?=[\s.,!?)]|$)/g, '$1$2')
    .replace(/`([^`\n]+)`/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^[\s]*[-*+]\s+/gm, '')
    .replace(/^>\s+/gm, '');
};

const AraChat = () => {
  const {
    messages, setMessages,
    input, setInput,
    isLoading, setIsLoading,
    sessionSaved, setSessionSaved,
    sessionId, sessionStartTime,
    sessionIdRef,
    discardSession,
    restoreSession,
  } = useAraActiveSession();
  const [isSaving, setIsSaving] = useState(false);
  const [supporterName, setSupporterName] = useState('');
  const [hasRestored, setHasRestored] = useState(false);
  const { encryptionKey, logout } = useAraAuth();
  const { theme, toggleTheme } = useAraTheme();
  const navigate = useNavigate();
  const endRef = useRef(null);
  const logo = theme === 'dark' ? '/ara-logo-dark.png' : '/ara-logo-light.png';

  useEffect(() => {
    (async () => {
      const name = await getAraAppState('ara_name');
      if (name) setSupporterName(name);
    })();
  }, []);

  // Returning-user restore: when AraChat mounts with no in-memory messages
  // and no restore attempted yet, load the most recent stored conversation.
  // This guarantees "reload / reopen restores the most recent conversation"
  // behavior without touching new-conversation starts.
  useEffect(() => {
    if (hasRestored) return;
    if (!encryptionKey) return;
    if (messages.length > 0) { setHasRestored(true); return; }
    (async () => {
      try {
        const all = await getAllAraEncrypted(ARA_STORES.SESSIONS, encryptionKey).catch(() => []);
        if (all && all.length > 0) {
          all.sort((a, b) => new Date(b.updatedAt || b.startedAt || 0) - new Date(a.updatedAt || a.startedAt || 0));
          restoreSession(all[0]);
        }
      } finally {
        setHasRestored(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [encryptionKey]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Auto-persist the ongoing conversation after each new message pair.
  // Writes an upsert keyed by sessionId → one conversation = one record,
  // no duplicates. Only writes when we have at least one user message.
  // Writes silently (no toast). Does NOT touch REFLECTIONS.
  const autoPersistConversation = async (nextMessages) => {
    if (!encryptionKey) return;
    if (!nextMessages || nextMessages.length === 0) return;
    try {
      const firstUser = nextMessages.find((m) => m.role === 'user');
      const titleRaw = firstUser?.content || 'Conversation';
      const title = titleRaw.length > 60 ? titleRaw.slice(0, 60).trim() + '…' : titleRaw;
      await saveAraEncrypted(ARA_STORES.SESSIONS, {
        id: sessionIdRef.current,
        title,
        startedAt: sessionStartTime,
        updatedAt: new Date().toISOString(),
        messages: nextMessages,
        messageCount: nextMessages.length,
      }, encryptionKey);
    } catch (e) {
      // Silent — persistence is best-effort; conversation stays in memory.
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;
    const userMsg = { role: 'user', content: input.trim(), timestamp: new Date().toISOString() };
    const current = input.trim();
    setInput('');

    const requestSessionId = sessionId;
    let history = [];
    setMessages((prev) => { history = [...prev, userMsg]; return history; });
    setSessionSaved(false);
    setIsLoading(true);

    try {
      const resp = await axios.post(`${BACKEND_URL}/api/chat`, {
        text: current,
        session_id: requestSessionId,
        message_history: history,
        persona: 'ara',
      });
      if (requestSessionId !== sessionIdRef.current) return;
      const assistantMsg = {
        role: 'assistant',
        content: resp.data.response,
        timestamp: new Date().toISOString(),
      };
      let finalMessages = history;
      setMessages((prev) => {
        finalMessages = [...prev, assistantMsg];
        return finalMessages;
      });
      // Auto-persist AFTER the Ara response completes — one write per exchange.
      await autoPersistConversation(finalMessages);
    } catch (err) {
      if (requestSessionId !== sessionIdRef.current) return;
      toast.error('Could not reach Ara right now');
    } finally {
      if (requestSessionId === sessionIdRef.current) setIsLoading(false);
    }
  };

  const startNewConversation = () => {
    // Starts a fresh conversation. Previous conversation remains in
    // AraSanctuaryDB.SESSIONS and can be reopened from "Conversations".
    discardSession();
    setHasRestored(true); // prevent auto-restore race from re-populating
  };

  const saveReflection = async () => {
    if (messages.length === 0) {
      toast('Nothing to save yet');
      return;
    }
    setIsSaving(true);
    try {
      const title = (() => {
        const firstUser = messages.find((m) => m.role === 'user');
        const t = firstUser?.content || 'Reflection';
        return t.length > 60 ? t.slice(0, 60).trim() + '…' : t;
      })();
      await saveAraEncrypted(ARA_STORES.REFLECTIONS, {
        id: sessionId,
        title,
        createdAt: new Date().toISOString(),
        startedAt: sessionStartTime,
        messages,
        messageCount: messages.length,
      }, encryptionKey);
      setSessionSaved(true);
      toast.success('Reflection saved');
    } catch (err) {
      toast.error('Could not save reflection');
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    discardSession();
    navigate('/ara');
  };

  return (
    <div className="ara-root min-h-screen flex flex-col" data-ara-theme={theme} data-testid="ara-chat">
      <header className="ara-header sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <img src={logo} alt="Ara" className="w-9 h-9 rounded-full flex-shrink-0" />
            <div className="min-w-0">
              <div className="font-serif font-semibold ara-text truncate">Ara</div>
              <div className="text-[11px] ara-text-muted truncate">
                {supporterName ? `with ${supporterName}` : 'The supporter\'s sanctuary'}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={startNewConversation}
              className="ara-icon-btn p-2 rounded-lg"
              title="Start a new conversation"
              data-testid="ara-new-conversation-btn"
              aria-label="Start a new conversation"
            >
              <Plus size={20} weight="duotone" />
            </button>
            <button
              onClick={() => navigate('/ara/conversations')}
              className="ara-icon-btn p-2 rounded-lg"
              title="Past conversations"
              data-testid="ara-nav-conversations-btn"
              aria-label="Past conversations"
            >
              <ChatCircleDots size={20} weight="duotone" />
            </button>
            <button
              onClick={() => navigate('/ara/reflections')}
              className="ara-icon-btn p-2 rounded-lg"
              title="Saved reflections"
              data-testid="ara-nav-reflections-btn"
              aria-label="Saved reflections"
            >
              <NotePencil size={20} weight="duotone" />
            </button>
            <button
              onClick={saveReflection}
              disabled={isSaving || messages.length === 0}
              className="ara-icon-btn p-2 rounded-lg disabled:opacity-40"
              title="Bookmark this as a reflection"
              data-testid="ara-save-reflection-btn"
              aria-label="Save reflection"
            >
              <BookmarkSimple size={20} weight={sessionSaved ? 'fill' : 'duotone'} />
            </button>
            <button
              onClick={() => navigate('/ara/preferences')}
              className="ara-icon-btn p-2 rounded-lg"
              title="About Me"
              data-testid="ara-nav-preferences-btn"
              aria-label="About Me"
            >
              <Gear size={20} weight="duotone" />
            </button>
            <button
              onClick={() => navigate('/support')}
              className="ara-icon-btn p-2 rounded-lg"
              title="Support AraVela"
              data-testid="ara-nav-support-btn"
              aria-label="Support AraVela"
            >
              <Heart size={20} weight="duotone" />
            </button>
            <button
              onClick={toggleTheme}
              className="ara-icon-btn p-2 rounded-lg"
              title="Toggle theme"
              data-testid="ara-chat-theme-toggle"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? <Sun size={20} weight="duotone" /> : <Moon size={20} weight="duotone" />}
            </button>
            <button
              onClick={handleLogout}
              className="ara-icon-btn p-2 rounded-lg"
              title="Lock Ara"
              data-testid="ara-logout-btn"
              aria-label="Lock Ara"
            >
              <SignOut size={20} weight="duotone" />
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-4 py-6">
          {messages.length === 0 ? (
            <div className="text-center py-12" data-testid="ara-empty-state">
              <img src={logo} alt="" className="w-24 h-24 mx-auto rounded-full mb-5 ara-logo-glow" />
              <h2 className="font-serif text-2xl font-semibold ara-text mb-2">
                {supporterName ? `Hello, ${supporterName}.` : 'Hello.'}
              </h2>
              <p className="ara-text-muted max-w-md mx-auto">
                Whatever you're carrying today — care, uncertainty, frustration, or
                tenderness — you can set it down here. What would you like to talk
                about?
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  data-testid={`ara-msg-${m.role}-${i}`}
                >
                  <div className={`max-w-[85%] rounded-2xl px-4 py-3 ${m.role === 'user' ? 'ara-msg-user' : 'ara-msg-assistant'}`}>
                    <p className="whitespace-pre-wrap leading-relaxed">
                      {m.role === 'assistant' ? stripMarkdown(m.content) : m.content}
                    </p>
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="flex justify-start" data-testid="ara-loading">
                  <div className="ara-msg-assistant rounded-2xl px-4 py-3 inline-flex items-center gap-2">
                    <Sparkle size={14} weight="duotone" className="animate-pulse" />
                    <span className="text-sm ara-text-muted">Ara is reflecting…</span>
                  </div>
                </div>
              )}
              <div ref={endRef} />
            </div>
          )}
        </div>
      </main>

      <footer className="ara-footer sticky bottom-0">
        <div className="max-w-3xl mx-auto px-4 py-3">
          <div className="flex items-end gap-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              placeholder="Share what's on your mind…"
              className="ara-input flex-1 rounded-2xl px-4 py-3 resize-none max-h-32"
              rows={1}
              data-testid="ara-chat-input"
            />
            <button
              onClick={sendMessage}
              disabled={!input.trim() || isLoading}
              className="ara-cta-primary rounded-full p-3 disabled:opacity-40"
              data-testid="ara-send-btn"
              aria-label="Send"
            >
              <PaperPlaneTilt size={20} weight="duotone" />
            </button>
          </div>
          <p className="text-[11px] ara-text-muted text-center mt-2">
            Your conversation saves on this device automatically. Tap the bookmark to mark something as a reflection.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default AraChat;
